// api.ts — increment API client. Token storage key is namespaced so it
// doesn't collide with other *.lab0.us SPAs sharing localStorage.
//
// onAuthInvalid wires the shared lib/web invalidateSession helper: any
// 401/403 from a request that actually sent credentials (Bearer or
// session cookie) is treated as "your session is no longer valid",
// printed to the console with the server's reason, and bounces the
// browser to /login (which auto-redirects through the OIDC flow).
import { createApiClient, invalidateSession } from 'common';

export const apiClient = createApiClient({
  tokenKey: 'increment_token',
  onAuthInvalid: ({ reason }) => {
    invalidateSession(apiClient, reason, {
      logoutPath: '/api/oidc/logout',
      redirectTo: '/login',
    });
  },
});

export interface Repo {
  id: number;
  owner: string;
  name: string;
  webhook_secret?: string; // only returned on create + rotate
  created_at: string;
}

export interface Commit {
  sha: string;
  message: string;
  author_name: string;
  author_email: string;
  authored_at?: string;
  parent_sha?: string;
  branches?: string[];
}

export interface MetricDef {
  id: number;
  name: string;
  unit: string;
  direction: 'lower_is_better' | 'higher_is_better';
  description: string;
  // 'scalar' = one value per commit; 'curve' = one value per run step.
  kind: 'scalar' | 'curve';
  created_at: string;
}

// Which way is "better" for a metric. Scalar metrics carry it on their
// def; a ValueOnCommit repeats it so the overview table can mark the better
// cell without a second /metrics fetch.
export type MetricDirection = 'lower_is_better' | 'higher_is_better';

// A dirty mark that covers a value: which mark, and the reason it gives.
export interface DirtyRef {
  id: number;
  reason: string;
  marked_by: string;
}

// Every value-bearing read says whether the value is dirty — not
// comparable to the rest — and which marks made it so. `dirty` is absent
// only on a server that predates dirty marks, which reads as clean.
export interface Dirtiness {
  dirty?: boolean;
  dirty_marks?: DirtyRef[];
}

// A stored dirty-mark rule on one experiment. kind=before covers the
// ancestors of `before` (and `before` itself when include_commit);
// kind=range covers git's from..to. Empty `metrics` covers every metric.
export interface DirtyMark {
  id: number;
  experiment_id: number;
  kind: 'before' | 'range';
  before?: string;
  include_commit: boolean;
  from?: string;
  to?: string;
  metrics: string[];
  reason: string;
  marked_by: string;
  marked_at: string;
  active: boolean;
  unmarked_by?: string;
  unmarked_at?: string;
  affected_values: number;
  affected_feeds: number;
}

export interface DirtyMarksResponse {
  marks: DirtyMark[] | null;
}

export interface ValueOnCommit extends Dirtiness {
  metric: string;
  unit: string;
  direction: MetricDirection;
  value: number;
  // Provenance, added by the increment_target rework: the bazel label that
  // produced the number, the digest of that target's source closure at the
  // commit, and — when the value was fanned out rather than measured here —
  // the sha actually measured.
  target?: string;
  dep_digest?: string;
  inherited_from?: string;
  submitted_by: string;
  submitted_at: string;
}

export interface SeriesPoint extends Dirtiness {
  sha: string;
  message: string;
  author_name: string;
  authored_at?: string;
  value: number;
  submitted_at: string;
  inherited_from?: string;
}

export interface SeriesResponse {
  metric: MetricDef;
  branch: string;
  points: SeriesPoint[];
}

export type MetricStatus = 'improved' | 'mixed' | 'regressed';

export interface GraphCommit extends Commit {
  branches: string[];
  metrics?: Record<string, number>;
  // Metric name → the marks making that value dirty; clean values are absent.
  dirty?: Record<string, DirtyRef[]>;
  // Computed from clean values only unless the graph was asked with
  // include_dirty=1.
  metric_status?: MetricStatus;
}

// A CI Job measuring a commit (GET /ci-jobs). The graph shows queued,
// running and failed ones as dots on the commit's row.
export type CIJobStatus = 'pending' | 'running' | 'failed' | 'succeeded';

export interface CIJob {
  name: string;
  commit: string;
  target?: string;
  branch?: string;
  run_url?: string;
  status: CIJobStatus;
  suspended?: boolean;
  failed_attempts: number;
  created_at: string;
  message?: string;
}

export interface CIJobsResponse {
  available: boolean;
  jobs: CIJob[] | null;
}

export interface GraphResponse {
  commits: GraphCommit[];
  branch_tips: Record<string, string>;
  // The experiment the ?experiment= filter resolved to, or null when the
  // graph was asked for repo-wide.
  experiment?: Experiment | null;
}

export interface RunMetric extends Dirtiness {
  name: string;
  points: number;
  last_step: number;
  last_value: number;
}

// A training run on a commit; carries curves (loss vs iter, …) keyed by
// curve-metric name.
export interface Run extends Dirtiness {
  id: number;
  sha: string;
  name: string;
  config: Record<string, unknown>;
  submitted_by: string;
  created_at: string;
  updated_at: string;
  message: string;
  author_name: string;
  authored_at?: string;
  branches: string[];
  metrics: RunMetric[];
}

export interface CurveSummary {
  points: number;
  first_step: number;
  last_step: number;
  last: number;
  min: number;
  min_step: number;
  max: number;
  max_step: number;
  tail_mean: number;
}

export interface RunCurve extends Dirtiness {
  run_id: number;
  points: [number, number][]; // [step, value], downsampled
  summary: CurveSummary | null; // over the full-resolution curve
}

export interface CurvesResponse {
  metric: MetricDef;
  curves: RunCurve[];
}

// ---------------------------------------------------------------------------
// Experiments — the top-level research object. An experiment owns one primary
// branch plus any branch absorbed from a consolidated child, declares the
// metrics it optimizes (position-ordered objectives), and has a lineage:
// fork makes a child, emancipate makes a root, consolidate folds a child back
// into its parent.
// ---------------------------------------------------------------------------

export type ExperimentStatus = 'active' | 'consolidated' | 'abandoned';

export interface ExperimentBranch {
  branch: string;
  role: 'primary' | 'absorbed';
  attached_at: string;
}

export interface ExperimentMetric {
  metric_id: number;
  name: string;
  unit: string;
  direction: MetricDirection;
  kind: 'scalar' | 'curve';
  objective: boolean;
  position: number;
}

export interface Experiment {
  id: number;
  repo_id: number;
  slug: string;
  title: string;
  hypothesis: string;
  parent_id?: number;
  status: ExperimentStatus;
  // `bazel run <label>` reproduces the experiment.
  replicate_target: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  // Primary branch first, then branches absorbed from consolidated children.
  branches: ExperimentBranch[] | null;
  // Position-ordered metrics this experiment optimizes; position 0 is the
  // headline metric the graph rows display.
  objectives: ExperimentMetric[] | null;
  commit_count: number;
  metric_count: number;
  feed_count: number;
  // Most recent of the experiment's own activity — a commit on one of its
  // branches, a metric value, a feed point. Drives the recency ordering and
  // the "3h ago" stamp in the experiments pane. Absent on a server that
  // predates the ordering work, which the pane renders as no stamp at all.
  last_activity_at?: string;
}

export interface ExperimentsResponse {
  experiments: Experiment[] | null;
}

// One selected commit in the multiselect overview pane: the commit, its
// metric values under the selected experiment, and its live data feeds.
// Feed curves are fetched separately (by run id) from /curves.
export interface CommitOverview {
  commit: Commit;
  values: ValueOnCommit[] | null;
  feeds: Run[] | null;
  // Absent on a server that predates artifacts.
  artifacts?: Artifact[] | null;
}

// A file a run uploaded at a commit (POST /artifacts). The bytes live in
// data.research; content_url is increment's streaming proxy for them, which
// is what the viewers read. mime_type picks the viewer
// (src/artifacts/registry.ts).
export interface Artifact {
  id: number;
  experiment_id: number;
  experiment: string;
  commit_sha: string;
  name: string;
  mime_type: string;
  size: number;
  sha256: string;
  target: string;
  metadata: Record<string, unknown> | null;
  object_key: string;
  uploaded_by: string;
  created_at: string;
  content_url: string;
  location?: string;
}

export interface OverviewResponse {
  experiment: Experiment | null;
  commits: CommitOverview[] | null;
}

export interface Identity {
  email: string;
  roles: string[];
  scopes: string[];
}

export function hasScope(scopes: string[] | undefined, want: string): boolean {
  if (!scopes) return false;
  return scopes.includes(want) || scopes.includes('authz.admin');
}
